import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Card, CardBody, CardHeader } from 'reactstrap';
import { Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Tooltip,
    Legend
} from 'chart.js';
import CompanyContext from '../../../auth/CompanyContext';
import Loading from '../../Loading/Loading';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const PortalPerformanceOverview = (props) => {
    const companyCtx = useContext(CompanyContext);
    const hasCompanyPortalDatabase = String(props.companySettings?.CompanyPortalDatabase || '').trim() !== '';

    const { callback, onWidgetLoad, onAddCallback } = props;
    const maxResults = 9;

    const getValues = useCallback((width) => {
        let col1 = 0, col2 = 0, graphHeight = 0;
        switch (width) {
            case 4:
                col1 = 12; col2 = 12; graphHeight = 350;
                break;
            case 6:
                col1 = 12; col2 = 12; graphHeight = 350;
                break;
            case 12:
                col1 = 12; col2 = 12; graphHeight = 350;
                break;
            default:
        }
        return { col1, col2, graphHeight };
    }, []);

    const initialValues = getValues(props.width);
    const [data, setData] = useState([]);
    const [labels, setLabels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dates, setDates] = useState([]);
    const [selectedDate, setSelectedDate] = useState('');
    const [col1, setCol1] = useState(initialValues.col1);
    const [col2, setCol2] = useState(initialValues.col2);
    const [graphHeight, setGraphHeight] = useState(initialValues.graphHeight);
    const [perfType, setPerfType] = useState('net');
    const [styleSettings, setStyleSettings] = useState({});
    const [min, setMin] = useState(0);
    const [irr, setIrr] = useState(false);
    const [chartFontFamily, setChartFontFamily] = useState(null);
    const [colors, setColors] = useState([]);
    const [returnText, setReturnText] = useState('Time Weighted Return');

    const refreshRef = useRef(() => { });
    const perfTypeRef = useRef('net');
    const irrRef = useRef(false);

    const getStyleSettings = useCallback(async () => {
        if (!hasCompanyPortalDatabase) return;

        const styleNames = [
            'chartDefaultFees',
            'chartColors',
            'pieChartColors',
            'perfHistIrr',
            'themeFontFamily',
            'themeChartFont'
        ];
        const params = new URLSearchParams();
        styleNames.forEach(name => params.append('styleNames[]', name));

        try {
            const results = await fetch(`${process.env.REACT_APP_PORTAL_API_URL}/api/crm/getStyleSettings?${params.toString()}`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.idToken || "",
                    'X-Database': companyCtx.company
                }
            });
            const response = await results.json();
            const nextStyleSettings = response.styleSettings || {};
            const chartColors = [];

            if (nextStyleSettings.chartColors) {
                nextStyleSettings.chartColors.split('|').forEach((colorLabelPair) => {
                    const pair = colorLabelPair.split('<');
                    chartColors.push({ color: pair[0], type: null, mapping: null });
                });
            }

            if (nextStyleSettings.pieChartColors) {
                nextStyleSettings.pieChartColors.split('|').forEach((colorLabelPair) => {
                    const pair = colorLabelPair.split('<');
                    chartColors.push({ color: pair[0], type: 'pie', mapping: pair[1] });
                });
            }

            const nextIrr = !!nextStyleSettings.perfHistIrr;
            const nextPerfType = nextStyleSettings.chartDefaultFees === 'Gross' ? 'gross' : 'net';

            perfTypeRef.current = nextPerfType;
            irrRef.current = nextIrr;

            setStyleSettings(nextStyleSettings);
            setPerfType(nextPerfType);
            setColors(chartColors);
            setIrr(nextIrr);
            setChartFontFamily(nextStyleSettings.themeChartFont || null);
            setReturnText(nextIrr ? 'IRR' : 'Time Weighted Return');
        } catch (err) {
            console.error(err);
            perfTypeRef.current = 'net';
            irrRef.current = false;

            setStyleSettings({});
            setPerfType('net');
            setColors([]);
            setIrr(false);
            setChartFontFamily(null);
            setReturnText('Time Weighted Return');
        }
    }, [props.idToken, companyCtx.company, hasCompanyPortalDatabase]);

    const getPerfDates = useCallback(async () => {
        if (!hasCompanyPortalDatabase) {
            setDates([]);
            setSelectedDate('');
            return '';
        }

        const payload = {
            email: props.user.user,
            port: null,
            household: props.selectedHousehold || null,
            perfType: perfTypeRef.current
        };

        try {
            const results = await fetch(`${process.env.REACT_APP_PORTAL_API_URL}/api/crm/perfDates`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.idToken || "",
                    'X-Database': companyCtx.company
                },
                body: JSON.stringify(payload)
            });
            const response = await results.json();
            if (response.code === 200) {
                const nextDates = response.data || [];
                const nextSelectedDate = nextDates.length > 0 ? nextDates[0].fDate : '';
                setDates(nextDates);
                setSelectedDate(nextSelectedDate);
                return nextDates.length > 0 ? nextDates[0].date : '';
            }
            setDates([]);
            setSelectedDate('');
            return '';
        } catch (err) {
            console.log(err);
            setDates([]);
            setSelectedDate('');
            return '';
        }
    }, [props.user.user, props.selectedHousehold, props.idToken, companyCtx.company, hasCompanyPortalDatabase]);

    const getTWRs = useCallback(async (dateValue) => {
        if (!hasCompanyPortalDatabase) {
            setLoading(false);
            setData([]);
            setLabels([]);
            return;
        }

        const payload = {
            port: null,
            household: props.selectedHousehold || null,
            date: dateValue || '',
            user: props.user.user,
            perfType: perfTypeRef.current,
            irr: irrRef.current,
            unmapped: props.user.unmapped ? 'true' : ''
        };

        const excludedCategories = new Set([
            'Prev Qtr',
            '3 Month',
            '6 Month',
            'YTD',
            'Prev Year',
            '1 Year',
            '3 Years',
            '5 Years',
            '7 Years',
            '10 Years',
            '15 Years',
            '20 Years',
            '25 Years'
        ]);

        try {
            const results = await fetch(`${process.env.REACT_APP_PORTAL_API_URL}/api/crm/perfOverviewWidget`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': 'Bearer ' + props.idToken || "",
                    'X-Database': companyCtx.company
                },
                body: JSON.stringify(payload)
            });
            const response = await results.json();

            if (response.code !== 200) {
                setLoading(false);
                setData([]);
                setLabels([]);
                setMin(0);
                alert('Could not retrieve performance data - Chart.');
                return;
            }

            const perfDataIn = Array.isArray(response.data) ? response.data : [];
            const perfData = [];
            let minValue = 0;
            const periodsWithData = [];

            for (let i = 0; i < perfDataIn.length; i++) {
                const sourceSeries = Array.isArray(perfDataIn[i]) ? perfDataIn[i] : [];
                const filteredSeries = sourceSeries.filter(item => !excludedCategories.has(item.category));

                for (let k = 0; k < filteredSeries.length; k++) {
                    const val = Number(filteredSeries[k] && filteredSeries[k].value);
                    if (Number.isFinite(val) && val < minValue) minValue = val;
                    if (val !== 0 && filteredSeries[k].value !== null && filteredSeries[k].value !== undefined) {
                        periodsWithData.push(filteredSeries[k].category);
                    }
                }

                perfData.push(filteredSeries);
            }

            const orderedPeriods = [];
            const seenPeriods = new Set();
            for (let i = 0; i < periodsWithData.length; i++) {
                const category = periodsWithData[i];
                if (!seenPeriods.has(category)) {
                    seenPeriods.add(category);
                    orderedPeriods.push(category);
                }
            }

            const filteredPerfData = perfData
                .map(series => series.filter(item => seenPeriods.has(item.category)))
                .filter(series => series.length > 0);

            setData(filteredPerfData);
            setLabels(orderedPeriods);
            setLoading(false);
            setMin(minValue);
        } catch (err) {
            console.log(err);
            setLoading(false);
            setData([]);
            setLabels([]);
            setMin(0);
        }
    }, [props.selectedHousehold, props.user.user, props.user.unmapped, props.idToken, companyCtx.company, hasCompanyPortalDatabase]);

    const onSubmit = useCallback(async () => {
        const dateValue = await getPerfDates();
        getTWRs(dateValue);
    }, [getPerfDates, getTWRs]);

    const runRefresh = useCallback(() => {
        setLoading(true);
        onSubmit();
    }, [onSubmit]);

    useEffect(() => {
        refreshRef.current = runRefresh;
    }, [runRefresh]);

    useEffect(() => {
        let active = true;

        const init = async () => {
            await getStyleSettings();
            if (!active) return;

            callback((width) => {
                if (!active) return;
                const values = getValues(width);
                setGraphHeight(values.graphHeight);
                setCol1(values.col1);
                setCol2(values.col2);
            });

            if (onWidgetLoad) {
                onWidgetLoad();
                onAddCallback(() => {
                    refreshRef.current();
                });
            }
        };

        init();

        return () => {
            active = false;
        };
    }, [hasCompanyPortalDatabase]);

    const getChartData = (seriesData) => {
        return {
            labels,
            datasets: seriesData.map((series, idx) => {
                const valuesByCategory = {};
                series.forEach(item => {
                    valuesByCategory[item.category] = Number(item.value) || 0;
                });

                return {
                    label: series[0] && series[0].accountPortfolio ? series[0].accountPortfolio : `Account ${idx + 1}`,
                    data: labels.map(category => valuesByCategory[category] !== undefined ? valuesByCategory[category] : null),
                    backgroundColor: colors[idx] && colors[idx].color ? colors[idx].color : undefined
                };
            })
        };
    };

    const chartOptions = useMemo(() => {
        const minValue = Number.isFinite(min) ? Math.min(0, min) : 0;
        return {
            responsive: true,
            maintainAspectRatio: false,
            plugins: {
                legend: {
                    display: false
                },
                tooltip: {
                    callbacks: {
                        title: (tooltipItems) => {
                            return tooltipItems && tooltipItems[0] ? tooltipItems[0].dataset.label : '';
                        },
                        label: (context) => {
                            const val = Number(context.raw);
                            if (!Number.isFinite(val)) return '';
                            return `${val.toFixed(2)}`;
                        }
                    }
                }
            },
            scales: {
                y: {
                    min: minValue,
                    title: {
                        display: false,
                        text: ''
                    }
                },
                x: {
                    ticks: {
                        font: chartFontFamily
                            ? { family: chartFontFamily }
                            : undefined
                    }
                }
            }
        };
    }, [min, chartFontFamily]);

    const series = [...data].slice(0, maxResults);
    const chartData = getChartData(series);
    const hasData = series.length > 0;
    const selectedDateValue = selectedDate ? new Date(selectedDate) : null;
    const selectedDateLabel = selectedDateValue && !Number.isNaN(selectedDateValue.getTime())
        ? selectedDateValue.toLocaleDateString('en-US')
        : '';

    void col1;
    void col2;
    void styleSettings;
    void dates;
    void perfType;
    void irr;
    void returnText;

    return (
        <Card className='w-100 m-0'>
            <CardHeader className="d-flex justify-content-between align-items-center">
                <h4>Performance Overview</h4>
                {props.icon}
            </CardHeader>
            <CardBody className='p-3'>
                {loading && <Loading />}
                {!loading && (
                    <>
                        {hasData ? (
                            <>
                                <div className='perfOverviewSnapshotHeader'>
                                    <label>
                                        {`As of ${selectedDateLabel}`}
                                        {series.length < data.length ? ` (Showing ${maxResults} of ${data.length} accounts)` : ''}
                                    </label>
                                </div>
                                <div style={{ height: graphHeight + 'px' }}>
                                    <Bar data={chartData} options={chartOptions} />
                                </div>
                            </>
                        ) : (
                            <div className='widgetNoData'>No data available</div>
                        )}
                    </>
                )}
            </CardBody>
        </Card>
    );
};

export default PortalPerformanceOverview;
